import type { Material, MaterialCategory } from './types'

/**
 * Записи о спортсмене и группе, материалы направления (этап 5 шаг 3, S20,
 * SC17): подписи и правила, общие для экранов, синтетики и тестов.
 */

/** Чьи записи: спортсмена или группы. Правила и экраны у них одни. */
export type NoteOwnerKind = 'athlete' | 'group'

export interface NoteOwner {
  kind: NoteOwnerKind
  id: string
}

/**
 * Типы записей - как их принимает сервер (`athlete_note`, `group_note`):
 * у группы «План», у спортсмена «Программа». Чужой тип сервер отвергнет.
 */
export const NOTE_KINDS: Record<NoteOwnerKind, [string, string][]> = {
  athlete: [
    ['general', 'Общее'],
    ['injury', 'Травма'],
    ['achievement', 'Достижение'],
    ['observation', 'Наблюдение'],
    ['program', 'Программа'],
  ],
  group: [
    ['general', 'Общее'],
    ['plan', 'План'],
    ['observation', 'Наблюдение'],
    ['achievement', 'Достижение'],
    ['injury', 'Травма'],
  ],
}

const KIND_LABEL: Record<string, string> = Object.fromEntries([...NOTE_KINDS.athlete, ...NOTE_KINDS.group])

/** Подпись типа записи; неизвестный тип - как есть. */
export function noteKindLabel(kind: string): string {
  return KIND_LABEL[kind] ?? kind
}

/** Предел заголовка, собранного из текста. */
export const NOTE_TITLE_MAX = 60

/**
 * Заголовок из текста: первая непустая строка, не длиннее 60 знаков. Сервер
 * требует заголовок, а тренеру в зале достаточно одного поля.
 */
export function titleFrom(content: string): string {
  const line = content
    .split('\n')
    .map((l) => l.trim())
    .find(Boolean)
  return (line ?? '').slice(0, NOTE_TITLE_MAX).trim()
}

/** Текст под заголовком: без первой строки, если заголовок из неё и собран. */
export function noteBody(n: { title: string; content: string }): string {
  const content = n.content.trim()
  if (content === n.title.trim()) return ''
  const lines = content.split('\n')
  const first = lines.findIndex((l) => l.trim())
  if (first >= 0 && lines[first].trim() === n.title.trim()) return lines.slice(first + 1).join('\n').trim()
  return content
}

export const MATERIAL_CATEGORIES: [MaterialCategory, string][] = [
  ['requirements', 'Требования'],
  ['standards', 'Нормативы'],
  ['rules', 'Правила'],
  ['general', 'Общее'],
]

const CATEGORY_LABEL = Object.fromEntries(MATERIAL_CATEGORIES) as Record<MaterialCategory, string>

export function categoryLabel(c: string): string {
  return CATEGORY_LABEL[c as MaterialCategory] ?? c
}

export function isCategory(c: string | null): c is MaterialCategory {
  return !!c && c in CATEGORY_LABEL
}

/** Теги из строки через запятую: без пустых и повторов, порядок сохраняется. */
export function parseTags(raw: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const t of raw.split(',')) {
    const tag = t.trim()
    const key = tag.toLowerCase()
    if (!tag || seen.has(key)) continue
    seen.add(key)
    out.push(tag)
  }
  return out
}

/** Поиск по материалам - как у Штаба (`SearchMaterials`): название, текст и теги. */
export function materialMatches(m: Pick<Material, 'title' | 'content' | 'tags'>, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return (
    m.title.toLowerCase().includes(q) || m.content.toLowerCase().includes(q) || m.tags.some((t) => t.toLowerCase().includes(q))
  )
}
